import React, { Component, ErrorInfo, ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import { Box, Typography, Button, Paper, Container } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import HomeIcon from '@mui/icons-material/Home';
import { API_URL } from '../config';
import { getAuthHeaders } from '../services/api';
import {
  attemptChunkLoadRecovery,
  CHUNK_RECOVERY_STABILITY_MS,
  clearChunkLoadRecoveryAttempt,
} from '../utils/chunkLoadRecovery';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  eventId: string | null;
}

class ErrorBoundary extends Component<Props, State> {
  private chunkRecoveryResetTimer?: number;

  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
    eventId: null,
  };

  public static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  public componentDidMount() {
    this.chunkRecoveryResetTimer = window.setTimeout(() => {
      clearChunkLoadRecoveryAttempt();
      this.chunkRecoveryResetTimer = undefined;
    }, CHUNK_RECOVERY_STABILITY_MS);
  }

  public componentWillUnmount() {
    this.cancelChunkRecoveryReset();
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // React.lazy failures are caught here rather than always reaching the
    // global error handlers. Reload once to fetch the current deployment; if
    // that deployment also fails, retain the normal manual recovery UI.
    this.cancelChunkRecoveryReset();
    attemptChunkLoadRecovery(error);

    this.setState({ errorInfo });

    // Log to Sentry with full context
    const eventId = Sentry.captureException(error, {
      extra: {
        componentStack: errorInfo.componentStack,
      },
    });
    this.setState({ eventId });

    // Also send to our backend alert system for ops visibility
    this.sendAlertToBackend(error, errorInfo);

    // Log to console for development
    console.error('🚨 ErrorBoundary caught an error:', error);
    console.error('📊 Component stack:', errorInfo.componentStack);
  }

  private cancelChunkRecoveryReset = () => {
    if (this.chunkRecoveryResetTimer !== undefined) {
      window.clearTimeout(this.chunkRecoveryResetTimer);
      this.chunkRecoveryResetTimer = undefined;
    }
  };

  private async sendAlertToBackend(error: Error, errorInfo: ErrorInfo) {
    try {
      const sessionId = sessionStorage.getItem('sessionId') || 'unknown';
      const userId = localStorage.getItem('userId') || 'unknown';
      
      await fetch(`${API_URL}/api/alerts/send`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
          'X-Session-ID': sessionId,
        },
        body: JSON.stringify({
          priority: 'P1_HIGH',
          message: `Frontend crash: ${error.message}`,
          details: {
            errorName: error.name,
            errorMessage: error.message,
            errorStack: error.stack?.substring(0, 2000),
            componentStack: errorInfo.componentStack?.substring(0, 2000),
            url: window.location.href,
            userAgent: navigator.userAgent,
            userId,
            timestamp: new Date().toISOString(),
          },
          timestamp: new Date().toISOString(),
          environment: process.env.REACT_APP_ENV || 'development',
        }),
      });
    } catch (alertError) {
      // Don't let alert failures cause more issues
      console.error('Failed to send error alert:', alertError);
    }
  }

  private handleRefresh = () => {
    window.location.reload();
  };

  private handleGoHome = () => {
    window.location.href = '/';
  };

  private handleReportFeedback = () => {
    if (this.state.eventId) {
      Sentry.showReportDialog({ eventId: this.state.eventId });
    }
  };

  public render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <Container maxWidth="sm">
          <Box
            sx={{
              minHeight: '100vh',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              py: 4,
            }}
          >
            <Paper
              elevation={3}
              sx={{
                p: 4,
                textAlign: 'center',
                borderRadius: 3,
                background: 'linear-gradient(135deg, #fff 0%, #f5f5f5 100%)',
              }}
            >
              <ErrorOutlineIcon
                sx={{
                  fontSize: 80,
                  color: '#f44336',
                  mb: 2,
                }}
              />
              
              <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, color: '#333' }}>
                Oops! Something went wrong
              </Typography>
              
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                We're sorry for the inconvenience. Our team has been notified and is working on a fix.
              </Typography>

              {process.env.REACT_APP_ENV !== 'production' && this.state.error && (
                <Paper
                  sx={{
                    p: 2,
                    mb: 3,
                    bgcolor: '#fff3e0',
                    borderLeft: '4px solid #ff9800',
                    textAlign: 'left',
                  }}
                >
                  <Typography variant="subtitle2" color="error" sx={{ fontWeight: 600 }}>
                    {this.state.error.name}: {this.state.error.message}
                  </Typography>
                  <Typography
                    variant="caption"
                    component="pre"
                    sx={{
                      mt: 1,
                      overflow: 'auto',
                      maxHeight: 200,
                      fontSize: '0.7rem',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}
                  >
                    {this.state.error.stack}
                  </Typography>
                </Paper>
              )}

              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  startIcon={<RefreshIcon />}
                  onClick={this.handleRefresh}
                  sx={{
                    bgcolor: '#2D2070',
                    '&:hover': { bgcolor: '#1a1445' },
                  }}
                >
                  Refresh Page
                </Button>
                
                <Button
                  variant="outlined"
                  startIcon={<HomeIcon />}
                  onClick={this.handleGoHome}
                  sx={{
                    borderColor: '#2D2070',
                    color: '#2D2070',
                    '&:hover': { borderColor: '#1a1445', bgcolor: 'rgba(45, 32, 112, 0.04)' },
                  }}
                >
                  Go Home
                </Button>

                {this.state.eventId && (
                  <Button
                    variant="text"
                    onClick={this.handleReportFeedback}
                    sx={{ color: '#666' }}
                  >
                    Report Feedback
                  </Button>
                )}
              </Box>

              {this.state.eventId && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 3, display: 'block' }}>
                  Error ID: {this.state.eventId}
                </Typography>
              )}
            </Paper>
          </Box>
        </Container>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
